import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { CaretRight } from 'phosphor-react';
import { RootState } from '@/store';
import { SAvatar } from '@/components/ui/atoms/SAvatar';
import { SBack } from '@/components/ui/molecules/SBack';
import { ThemeToggle } from '@/components/ui/atoms/ThemeToggle';
import SDialog from '@/components/ui/molecules/SDialog';
import { AvatarEditDrawer } from '@/components/ui/organisms/AvatarEditDrawer';
import { AvatarZoomModal } from '@/components/ui/organisms/AvatarZoomModal';
import { AuthService } from '@/services/auth.service';
import memberService from '@/services/member.service';
import { logout as logoutAction } from '@/store/authSlice';
import { useMember } from '@/hooks/useMember';
import { useSupportContact } from '@/hooks/useSupportContact';
import { sendLogoutMessage } from '@/utils/nativeBridge';
import './ProfileNavigation.css';

const ProfileNavigation: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const user = useSelector((state: RootState) => state.auth.user);
  const { removeAvatar } = useMember();
  const support = useSupportContact();

  const [showSignOutDialog, setShowSignOutDialog]       = useState(false);
  const [showDeleteDialog, setShowDeleteDialog]         = useState(false);
  const [showDeleteAvatarDialog, setShowDeleteAvatarDialog] = useState(false);
  const [showAvatarEditDrawer, setShowAvatarEditDrawer] = useState(false);
  const [showAvatarZoomModal, setShowAvatarZoomModal]   = useState(false);
  const [selectedImageFile, setSelectedImageFile]       = useState<File | null>(null);
  const [avatarUrl, setAvatarUrl]                       = useState('');

  useEffect(() => {
    memberService.getAvatarUrl()
      .then(r => setAvatarUrl(r.url))
      .catch(e => { if (e?.response?.status !== 400) console.error(e); });
  }, []);

  const handleSaveAvatar = async (blob: Blob) => {
    try {
      await memberService.uploadAvatar(new File([blob], 'avatar.jpg', { type: 'image/jpeg' }));
      const r = await memberService.getAvatarUrl();
      setAvatarUrl(r.url);
    } catch (e) { console.error(e); }
  };

  const handleConfirmDeleteAvatar = () => {
    removeAvatar(undefined, {
      onSuccess: () => { setAvatarUrl(''); setShowDeleteAvatarDialog(false); },
      onError:   () => { setShowDeleteAvatarDialog(false); },
    });
  };

  const handleConfirmSignOut = async () => {
    try {
      sendLogoutMessage();
      await new Promise(r => setTimeout(r, 100));
      await AuthService.logout();
    } catch {}
    dispatch(logoutAction());
    const welcomeName = localStorage.getItem('welcomeName');
    localStorage.clear();
    if (welcomeName) localStorage.setItem('welcomeName', welcomeName);
    // Hard redirect (not navigate()) so no stale in-memory/router state — e.g. a
    // swapped booking location — survives into the next member's session.
    window.location.href = '/login';
  };

  const handleConfirmDelete = async () => {
    // TODO: implement delete account
    setShowDeleteDialog(false);
  };

  const fullName = user?.firstName && user?.lastName
    ? `${user.firstName} ${user.lastName}` : 'User Name';
  const email  = user?.email   || '';
  const mobile = user?.mobile  || '';

  return (
    <div className="pn">
      {/* Header */}
      <div className="pn__header">
        <SBack onClick={() => navigate('/home', { replace: true })} />
        <span className="pn__title">Account</span>
        <ThemeToggle />
      </div>

      {/* Avatar */}
      <div className="pn__avatar">
        <SAvatar
          variant="profile"
          editable
          onEditClick={() => setShowAvatarEditDrawer(true)}
          avatarUrl={avatarUrl}
        />
        <h2 className="pn__name">{fullName}</h2>
        {email  && <p className="pn__email">{email}</p>}
        {mobile && <p className="pn__mobile">{mobile}</p>}
      </div>

      {/* Nav sections */}
      <div className="pn__body">

        {/* Profile */}
        <div>
          <p className="pn__section-label">Profile</p>
          <div className="pn__group">
            <button className="pn__row" onClick={() => navigate('/profile/personal-information')}>
              <span className="pn__row-label">Personal Information</span>
              <CaretRight size={16} color="#9e9a90" />
            </button>
            <div className="pn__divider" />
            <button className="pn__row" onClick={() => navigate('/profile/membership')}>
              <span className="pn__row-label">Membership</span>
              <CaretRight size={16} color="#9e9a90" />
            </button>
            <div className="pn__divider" />
            <button className="pn__row" onClick={() => navigate('/profile/orders')}>
              <span className="pn__row-label">My Orders</span>
              <CaretRight size={16} color="#9e9a90" />
            </button>
            <div className="pn__divider" />
            <button className="pn__row" onClick={() => navigate('/profile/notification-preference')}>
              <span className="pn__row-label">Notification Preferences</span>
              <CaretRight size={16} color="#9e9a90" />
            </button>
          </div>
        </div>

        {/* Help */}
        <div>
          <p className="pn__section-label">Help Center</p>
          <div className="pn__group">
            {/* The member's own box, not a national inbox — see
                useSupportContact for the fallback order. */}
            <button className="pn__row" onClick={() => { globalThis.location.href = support.emailHref; }}>
              <span className="pn__row-label">Email Us</span>
              <span className="pn__row-value">{support.email}</span>
            </button>
            <div className="pn__divider" />
            <button className="pn__row" onClick={() => { globalThis.location.href = support.phoneHref; }}>
              <span className="pn__row-label">Call Us</span>
              <span className="pn__row-value">{support.phone}</span>
            </button>
            <div className="pn__divider" />
            <button className="pn__row" onClick={() => window.open('https://tbx.golf/about/faqs', '_blank')}>
              <span className="pn__row-label">FAQs</span>
              <CaretRight size={16} color="#9e9a90" />
            </button>
          </div>
        </div>

        {/* About */}
        <div>
          <p className="pn__section-label">About</p>
          <div className="pn__group">
            <button className="pn__row" onClick={() => window.open('https://tbx.golf/terms-of-use', '_blank')}>
              <span className="pn__row-label">Terms of Service</span>
              <CaretRight size={16} color="#9e9a90" />
            </button>
            <div className="pn__divider" />
            <button className="pn__row" onClick={() => window.open('https://tbx.golf/privacy-policy', '_blank')}>
              <span className="pn__row-label">Privacy Policy</span>
              <CaretRight size={16} color="#9e9a90" />
            </button>
            <div className="pn__divider" />
            <div className="pn__row" style={{ cursor: 'default' }}>
              <span className="pn__row-label">App Version</span>
              <span className="pn__row-value">v5.0.0</span>
            </div>
          </div>
        </div>

        {/* Sign out */}
        <button className="pn__signout" onClick={() => setShowSignOutDialog(true)}>
          Sign Out
        </button>

      </div>

      {/* Dialogs */}
      <SDialog
        isOpen={showSignOutDialog}
        onClose={() => setShowSignOutDialog(false)}
        title="Sign out?"
        onConfirm={handleConfirmSignOut}
        confirmText="Sign Out"
        cancelText="Cancel"
      />
      <SDialog
        isOpen={showDeleteDialog}
        onClose={() => setShowDeleteDialog(false)}
        title="Delete account?"
        onConfirm={handleConfirmDelete}
        confirmText="Delete"
        cancelText="Cancel"
      >
        <p>This action cannot be undone. Your account and all associated data will be permanently deleted.</p>
      </SDialog>
      <SDialog
        isOpen={showDeleteAvatarDialog}
        onClose={() => setShowDeleteAvatarDialog(false)}
        title="Remove photo?"
        onConfirm={handleConfirmDeleteAvatar}
        confirmText="Remove"
        cancelText="Cancel"
      />

      {/* Avatar drawers */}
      <AvatarEditDrawer
        isOpen={showAvatarEditDrawer}
        onClose={() => setShowAvatarEditDrawer(false)}
        onTakePhoto={f => { setSelectedImageFile(f); setShowAvatarZoomModal(true); }}
        onChooseFromLibrary={f => { setSelectedImageFile(f); setShowAvatarZoomModal(true); }}
        onDeletePhoto={() => setShowDeleteAvatarDialog(true)}
      />
      <AvatarZoomModal
        isOpen={showAvatarZoomModal}
        onClose={() => setShowAvatarZoomModal(false)}
        imageFile={selectedImageFile}
        onSave={handleSaveAvatar}
      />
    </div>
  );
};

export default ProfileNavigation;
